import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWhatsOnDeck,
  refreshWhatsOnDeck,
  getProjectRisk,
  refreshProjectRisk,
  getRiskHistory,
  generateStatusUpdate,
  getMyInvolvements,
  getTodayBriefing,
  refreshTodayBriefing,
} from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { useUserStore } from '../store/userStore';
import type { StatusUpdateRequest } from '../types';

export function useWhatsOnDeck() {
  return useQuery({
    queryKey: queryKeys.whatsOnDeck,
    queryFn: getWhatsOnDeck,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProjectRisk(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.risk(projectId ?? ''),
    queryFn: () => getProjectRisk(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRiskHistory(projectId: string | undefined, days?: number) {
  return useQuery({
    queryKey: queryKeys.riskHistory(projectId ?? '', days),
    queryFn: () => getRiskHistory(projectId!, days),
    enabled: !!projectId,
  });
}

export function useRefreshRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => refreshProjectRisk(projectId),
    onSuccess: (_data, projectId) => {
      // Invalidate risk cache and history
      void qc.invalidateQueries({ queryKey: queryKeys.risk(projectId) });
      void qc.invalidateQueries({ queryKey: queryKeys.riskHistory(projectId) });
      // Also invalidate the project and project list so overallRiskScore stays
      // in sync — prevents delivery confidence from differing across views
      void qc.invalidateQueries({ queryKey: queryKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
}

export function useRefreshWhatsOnDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: refreshWhatsOnDeck,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.whatsOnDeck });
    },
  });
}

export function useGenerateStatusUpdate() {
  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: StatusUpdateRequest;
    }) => generateStatusUpdate(projectId, data),
  });
}

export function useTodayBriefing(role: string) {
  const qc = useQueryClient();
  // useEffectiveRole() always returns *some* FunctionalRole (it has no
  // "not yet known" state), so gating on `!!role` here was always true —
  // the query fired before identity had loaded. sessionConfig is the
  // actual signal that the caller's identity is known.
  const sessionConfig = useUserStore((s) => s.sessionConfig);

  return useQuery({
    queryKey: queryKeys.todayBriefing(role),
    queryFn: async () => {
      const data = await getTodayBriefing(role);
      // The server resolves the caller's persona independently and may
      // substitute a different (but always caller-owned) role than the one
      // requested — see DailyBriefing.role. When it does, mirror the
      // response into the correctly-keyed cache slot so the client keys on
      // the resolved truth rather than its own guess: later reads for that
      // persona hit real data instead of a redundant fetch, and the
      // mismatch is at least visible in the cache instead of silently
      // living under the wrong key forever.
      if (data && data.role !== role) {
        qc.setQueryData(queryKeys.todayBriefing(data.role), data);
      }
      return data;
    },
    enabled: !!sessionConfig,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRefreshBriefing(role: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refreshTodayBriefing(role),
    onSuccess: (data) => {
      // A cooldown response means nothing was regenerated — invalidating
      // (and refetching) here would just be a wasted round trip for
      // identical data. See DailyBriefing's cooldown surfacing in
      // TodayView, which reads this mutation's `data` directly.
      if (data.cooldown) return;
      void qc.invalidateQueries({ queryKey: queryKeys.todayBriefing(role) });
    },
  });
}

export function useMyInvolvements() {
  return useQuery({
    queryKey: ['my-involvements'],
    queryFn: getMyInvolvements,
    staleTime: 5 * 60 * 1000,
  });
}
